import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fade,
  Divider,
} from '@mui/material';
import {
  Close,
  Add,
  Delete,
  Refresh,
  Save,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { getSemanticLayer, updateSemanticLayer, SemanticLayerResponse } from '../api/admin';
import { useTheme, getColors } from '../components/ThemeContext';

const DARK_COLORS = {
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  SUCCESS: "#4caf50",
  WARNING: "#ff9800",
  ERROR: "#ff4444",
};

interface SemanticLayerManagerProps {
  onClose?: () => void;
  tenant?: string;
}

export const SemanticLayerManager: React.FC<SemanticLayerManagerProps> = ({
  onClose,
  tenant = 'camngo'
}) => {
  const COLORS = DARK_COLORS;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SemanticLayerResponse | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [aliases, setAliases] = useState<Array<{ alias: string; canonical: string }>>([]);
  const [newAlias, setNewAlias] = useState('');
  const [newCanonical, setNewCanonical] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSemanticLayer(tenant);
      setData(response);
      setEtag(response.meta.etag);
      setAliases(response.data.aliases || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load semantic layer data');
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddAlias = () => {
    if (newAlias.trim() && newCanonical.trim()) {
      setAliases([...aliases, { alias: newAlias.trim(), canonical: newCanonical.trim() }]);
      setNewAlias('');
      setNewCanonical('');
    }
  };

  const handleDeleteAlias = (index: number) => {
    setAliases(aliases.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!etag) {
      setError('ETag is missing. Please refresh the data first.');
      return;
    }

    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const response = await updateSemanticLayer(tenant, { data: { aliases } }, etag);
      setData(response);
      setEtag(response.meta.etag);
      setError(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      if (err.message === 'CONFLICT: File was modified by another user. Please refresh and try again.') {
        setError('Conflict: The file was modified by another user. Please refresh and try again.');
      } else if (err.message === 'PRECONDITION_REQUIRED: If-Match header is required for existing files.') {
        setError('Precondition required: Please refresh the data first.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to save semantic layer data');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: COLORS.PRIMARY,
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          background: `linear-gradient(135deg, ${COLORS.SECONDARY} 0%, ${COLORS.ACCENT} 100%)`,
          borderBottom: `1px solid ${COLORS.BORDER}`,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              color: COLORS.TEXT_PRIMARY, 
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
          >
            Semantic Layer Manager
          </Typography>
          <Chip
            label={tenant}
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: COLORS.TEXT_PRIMARY,
              border: `1px solid ${COLORS.BORDER}`,
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
            }}
          />
        </Box>
        {onClose && (
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: COLORS.TEXT_SECONDARY,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: COLORS.TEXT_PRIMARY,
              },
              transition: 'all 0.2s',
            }}
          >
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.TEXT_PRIMARY }} />
          </Box>
        ) : (
          <>
            {error && (
              <Fade in={!!error}>
                <Alert
                  severity={error.includes('Conflict') ? 'warning' : 'error'}
                  sx={{ 
                    mb: 3, 
                    backgroundColor: COLORS.ACCENT, 
                    color: COLORS.TEXT_PRIMARY,
                    border: `1px solid ${error.includes('Conflict') ? COLORS.WARNING : COLORS.ERROR}`,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  }}
                  action={
                    error.includes('Conflict') && (
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={handleRefresh}
                        sx={{ fontWeight: 600 }}
                      >
                        Refresh
                      </Button>
                    )
                  }
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {saveSuccess && (
              <Fade in={saveSuccess}>
                <Alert
                  severity="success"
                  icon={<CheckCircle />}
                  sx={{ 
                    mb: 3, 
                    backgroundColor: COLORS.ACCENT, 
                    color: COLORS.TEXT_PRIMARY,
                    border: `1px solid ${COLORS.SUCCESS}`,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                  }}
                >
                  Changes saved successfully!
                </Alert>
              </Fade>
            )}

            {data && etag && (
              <Paper
                elevation={0}
                sx={{ 
                  mb: 3, 
                  p: 2.5,
                  backgroundColor: COLORS.ACCENT,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.BORDER}`,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY }}>
                    Last Modified: <span style={{ color: COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                      {new Date(data.meta.last_modified).toLocaleString()}
                    </span>
                  </Typography>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: COLORS.BORDER }} />
                  <Chip
                    label={`ETag: ${etag.substring(0, 12)}...`}
                    size="small"
                    sx={{
                      backgroundColor: COLORS.SECONDARY,
                      color: COLORS.TEXT_SECONDARY,
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      border: `1px solid ${COLORS.BORDER}`,
                    }}
                  />
                  <Tooltip title="Refresh data" arrow>
                    <IconButton
                      size="small"
                      onClick={handleRefresh}
                      sx={{ 
                        color: COLORS.TEXT_SECONDARY,
                        '&:hover': {
                          backgroundColor: COLORS.HOVER,
                          color: COLORS.TEXT_PRIMARY,
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            )}

            {/* Add New Alias Section */}
            <Paper
              elevation={0}
              sx={{ 
                mb: 3, 
                p: 3,
                backgroundColor: COLORS.ACCENT,
                borderRadius: 2,
                border: `1px solid ${COLORS.BORDER}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 2, 
                  color: COLORS.TEXT_PRIMARY,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Add sx={{ fontSize: 20 }} />
                Add New Alias
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alias"
                  placeholder="Enter alias name..."
                  value={newAlias}
                  onChange={(e) => setNewAlias(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newAlias.trim() && newCanonical.trim()) {
                      handleAddAlias();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: COLORS.TEXT_PRIMARY,
                      backgroundColor: COLORS.SECONDARY,
                      borderRadius: 1.5,
                      '& fieldset': { 
                        borderColor: COLORS.BORDER,
                        borderWidth: 1,
                      },
                      '&:hover fieldset': { 
                        borderColor: COLORS.TEXT_SECONDARY,
                      },
                      '&.Mui-focused fieldset': { 
                        borderColor: COLORS.TEXT_PRIMARY,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: COLORS.TEXT_SECONDARY,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Canonical"
                  placeholder="Enter canonical name..."
                  value={newCanonical}
                  onChange={(e) => setNewCanonical(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newAlias.trim() && newCanonical.trim()) {
                      handleAddAlias();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: COLORS.TEXT_PRIMARY,
                      backgroundColor: COLORS.SECONDARY,
                      borderRadius: 1.5,
                      '& fieldset': { 
                        borderColor: COLORS.BORDER,
                        borderWidth: 1,
                      },
                      '&:hover fieldset': { 
                        borderColor: COLORS.TEXT_SECONDARY,
                      },
                      '&.Mui-focused fieldset': { 
                        borderColor: COLORS.TEXT_PRIMARY,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: COLORS.TEXT_SECONDARY,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddAlias}
                  disabled={!newAlias.trim() || !newCanonical.trim()}
                  sx={{
                    minWidth: '56px',
                    height: '40px',
                    backgroundColor: COLORS.TEXT_PRIMARY,
                    color: COLORS.PRIMARY,
                    borderRadius: 1.5,
                    boxShadow: '0 2px 8px rgba(255, 255, 255, 0.1)',
                    '&:hover': { 
                      backgroundColor: COLORS.TEXT_SECONDARY,
                      boxShadow: '0 4px 12px rgba(255, 255, 255, 0.15)',
                    },
                    '&:disabled': {
                      backgroundColor: COLORS.SECONDARY,
                      color: COLORS.TEXT_SECONDARY,
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <Add />
                </Button>
              </Box>
            </Paper>

            {/* Table */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                backgroundColor: COLORS.ACCENT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: 2,
                maxHeight: 450,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                // Allow scrolling through long alias lists (required for stickyHeader tables).
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        backgroundColor: COLORS.SECONDARY, 
                        color: COLORS.TEXT_PRIMARY,
                        fontWeight: 600,
                        borderBottom: `2px solid ${COLORS.BORDER}`,
                        py: 2,
                      }}
                    >
                      Alias
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        backgroundColor: COLORS.SECONDARY, 
                        color: COLORS.TEXT_PRIMARY,
                        fontWeight: 600,
                        borderBottom: `2px solid ${COLORS.BORDER}`,
                        py: 2,
                      }}
                    >
                      Canonical
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ 
                        backgroundColor: COLORS.SECONDARY, 
                        color: COLORS.TEXT_PRIMARY,
                        fontWeight: 600,
                        borderBottom: `2px solid ${COLORS.BORDER}`,
                        py: 2,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aliases.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={3} 
                        align="center" 
                        sx={{ 
                          color: COLORS.TEXT_SECONDARY,
                          py: 6,
                          backgroundColor: COLORS.ACCENT,
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Warning sx={{ fontSize: 48, opacity: 0.3 }} />
                          <Typography variant="body1">No aliases found</Typography>
                          <Typography variant="caption">Add your first alias above</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    aliases.map((item, index) => (
                      <TableRow 
                        key={index} 
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: COLORS.HOVER,
                          },
                          transition: 'background-color 0.2s',
                          borderBottom: `1px solid ${COLORS.BORDER}`,
                        }}
                      >
                        <TableCell 
                          sx={{ 
                            color: COLORS.TEXT_PRIMARY,
                            fontWeight: 500,
                            py: 2,
                          }}
                        >
                          {item.alias}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            color: COLORS.TEXT_PRIMARY, 
                            fontFamily: 'monospace',
                            fontSize: '0.9em',
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            py: 2,
                          }}
                        >
                          {item.canonical}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Tooltip title="Delete alias" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAlias(index)}
                              sx={{ 
                                color: COLORS.ERROR,
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 68, 68, 0.1)',
                                },
                                transition: 'all 0.2s',
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {data && (
              <Paper
                elevation={0}
                sx={{ 
                  mt: 3, 
                  p: 2, 
                  backgroundColor: 'rgba(255, 152, 0, 0.05)',
                  borderRadius: 2,
                  border: `1px solid rgba(255, 152, 0, 0.2)`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Warning sx={{ color: COLORS.WARNING, fontSize: 20, mt: 0.2 }} />
                  <Typography variant="caption" sx={{ color: COLORS.TEXT_SECONDARY, lineHeight: 1.6 }}>
                    Using ETag <code style={{ 
                      backgroundColor: COLORS.SECONDARY, 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      color: COLORS.TEXT_PRIMARY,
                    }}>{etag?.substring(0, 12)}...</code> for safe updates. 
                    If the file was modified by another user, you'll get a conflict error and need to refresh.
                  </Typography>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* Footer with gradient */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          p: 3,
          background: `linear-gradient(135deg, ${COLORS.ACCENT} 0%, ${COLORS.SECONDARY} 100%)`,
          borderTop: `1px solid ${COLORS.BORDER}`,
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Button
          onClick={handleRefresh}
          startIcon={<Refresh />}
          sx={{ 
            color: COLORS.TEXT_PRIMARY,
            borderRadius: 1.5,
            px: 3,
            '&:hover': {
              backgroundColor: COLORS.HOVER,
            },
            transition: 'all 0.2s',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Refresh
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!etag || saving || loading}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: COLORS.PRIMARY }} /> : <Save />}
          sx={{
            backgroundColor: COLORS.TEXT_PRIMARY,
            color: COLORS.PRIMARY,
            borderRadius: 1.5,
            px: 4,
            boxShadow: '0 2px 8px rgba(255, 255, 255, 0.1)',
            '&:hover': { 
              backgroundColor: COLORS.TEXT_SECONDARY,
              boxShadow: '0 4px 12px rgba(255, 255, 255, 0.15)',
            },
            '&:disabled': { 
              backgroundColor: COLORS.SECONDARY,
              color: COLORS.TEXT_SECONDARY,
              boxShadow: 'none',
            },
            transition: 'all 0.2s',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};
